import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import TinyColor from 'tinycolor2';
import Link from 'components/core/link';

import { ProductCardButtonOption, ProductCardTextAlignmentOption } from 'types/graphql';

import { StarCheck } from 'src/assets/star-check';

import { EmbeddedExternalLink } from 'src/components/core';
import { CollectionBadge } from '../components/collection-card-badge';
import HoverImageGallery from '../components/hover-image-gallery';
import { SpecialOfferRotator } from '../components/special-offer-rotator';
import { CardViewProps } from '../types';

/**
 * Pure presentational grid card component.
 * Receives all data and callbacks via props -- no hooks or store access.
 */
export const CardView = ({
  product: { name, brandName, href, strainType, potency },
  image,
  badges: { isStaffPick, isSponsored, isSpecialOffer, specialNames, hideSalesLanguage, collectionBadge },
  pricing: { displayPrice, standardPrice, discount, showWeight, weight, buttonCopy },
  layout: { isEmbeddedCarousel, fullWidthImages, textAlignment, hideSponsoredTag },
  onLinkClick,
  onAddToCart,
  containerRef,
  productIndex,
  'data-testid': testId,
  isSmallCard,
  isCarousel,
}: CardViewProps) => {
  const ImageWrapper = fullWidthImages ? ImageWrapperFullWidth : ImageWrapperFramed;

  return (
    <Wrapper
      ref={containerRef}
      data-testid={testId}
      data-placement-index={productIndex}
      $isCentered={textAlignment === ProductCardTextAlignmentOption.center}
      $smallCard={isSmallCard}
      $isCarousel={isCarousel}
    >
      <LinkComponent href={href} handleLinkClick={onLinkClick} isEmbeddedCarousel={isEmbeddedCarousel}>
        <div>
          <ImageWrapper>
            <HoverImageGallery
              {...image}
              height={fullWidthImages ? 231 : 188}
              width={fullWidthImages ? 231 : 188}
              alt={`Image of ${name} product`}
            />
            {isStaffPick && (
              <StaffPick>
                <StarCheck />
                Staff Pick
              </StaffPick>
            )}
          </ImageWrapper>

          {isSponsored && !hideSponsoredTag && <Sponsored>Sponsored</Sponsored>}

          <Name>{name}</Name>

          <Brand>{brandName || ' '}</Brand>
        </div>

        <div>
          {(!!strainType || potency.length > 0) && (
            <Details>
              {strainType && <Strain>{strainType}</Strain>}
              {potency.length > 0 && <Potency $isMissingStrain={!strainType}>{potency.join(' · ')}</Potency>}
            </Details>
          )}

          {(isSpecialOffer || collectionBadge) && (
            <Tags>
              {isSpecialOffer && (
                <SpecialOfferRotator specialNames={specialNames} hideSalesLanguage={hideSalesLanguage} />
              )}

              {collectionBadge && <CollectionBadge {...collectionBadge} />}
            </Tags>
          )}
        </div>
      </LinkComponent>
      <div>
        <Price>
          <ActivePrice>
            {displayPrice}
            {showWeight && <Weight> - {weight}</Weight>}
          </ActivePrice>

          {discount && (
            <DiscountedPrice>
              <OriginalPrice>{standardPrice}</OriginalPrice>
              <Discount>{discount}</Discount>
            </DiscountedPrice>
          )}
        </Price>

        {isEmbeddedCarousel ? (
          <EmbeddedButton href={href} data-testid='card-button'>
            View Item
          </EmbeddedButton>
        ) : (
          <Button onClick={onAddToCart} data-testid='card-button'>
            {buttonCopy}
          </Button>
        )}
      </div>
    </Wrapper>
  );
};

type LinkComponentProps = {
  children?: ReactNode;
  isEmbeddedCarousel: boolean;
  href: string;
  handleLinkClick: () => void;
};

const LinkComponent = ({ children, isEmbeddedCarousel, href, handleLinkClick }: LinkComponentProps) => {
  const testId = 'card-link';

  if (isEmbeddedCarousel) {
    return (
      <EmbeddedCarouselAnchor href={href} data-testid={testId}>
        {children}
      </EmbeddedCarouselAnchor>
    );
  }

  return (
    <Link href={href} onClick={handleLinkClick} data-testid={testId}>
      <AnchorWrapper>{children}</AnchorWrapper>
    </Link>
  );
};

const Wrapper = styled.div<{ $isCentered?: boolean; $smallCard?: boolean; $isCarousel?: boolean }>`
  --text-alignment: ${({ $isCentered }) => ($isCentered ? 'center' : 'left')};
  --justify-content: ${({ $isCentered }) => ($isCentered ? 'center' : 'flex-start')};

  background: #fff;
  border: 1px solid #e3e7e9;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  position: relative;
  text-align: var(--text-alignment);
  transition: all 0.2s;
  width: ${({ $isCarousel }) => ($isCarousel ? '184px' : '100%')};

  @media screen and (min-width: 1110px) {
    width: ${({ $isCarousel, $smallCard }) => {
      if (!$isCarousel) return '100%';
      return $smallCard ? '184px' : '220px';
    }};
  }

  &:hover {
    border-color: #d2d7da;
    box-shadow: 0 4px 10px 0 #0000000d;
  }
`;

const anchorStyles = css`
  display: block;

  /* Increase click area to entire card */
  &::before {
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const AnchorWrapper = styled.div`
  ${anchorStyles}
`;

const EmbeddedCarouselAnchor = styled(EmbeddedExternalLink)`
  ${anchorStyles}
`;

const ImageWrapperFramed = styled.div`
  aspect-ratio: 1 / 1;
  margin-bottom: 8px;
  position: relative;

  img {
    display: block;
    margin: 0 auto;
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`;

const StaffPick = styled.div`
  align-items: center;
  background: #fff;
  border-bottom-right-radius: ${({ theme }) => theme.customized.radius.xs};
  color: ${({ theme }) => theme.customized.colors.staffPickTag};
  display: flex;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  left: 0;
  line-height: ${13 / 12};
  padding: 4px;
  position: absolute;
  text-transform: ${({ theme }) => theme.customized.textTransforms.tags};
  top: 0;
`;

const ImageWrapperFullWidth = styled.div`
  aspect-ratio: 1 / 1;
  margin: -16px -16px 8px -16px;
  position: relative;
  border-top-left-radius: ${({ theme }) => theme.customized.radius.lg};
  border-top-right-radius: ${({ theme }) => theme.customized.radius.lg};
  overflow: hidden;

  img {
    display: block;
    margin: 0 auto;
    max-height: 100%;
    max-width: 100%;
    object-fit: cover;
  }

  ${StaffPick} {
    top: 16px;
    left: 16px;
  }
`;

const Sponsored = styled.div`
  color: #828a8f;
  font-size: 10px;
  line-height: 1;
  margin-bottom: 6px;
`;

const Name = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #121516;
  display: -webkit-box;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
  overflow: hidden;
`;

const Brand = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  color: #646d72;
  display: -webkit-box;
  font: 12px/1 ${({ theme }) => theme.customized.fonts.secondary};
  margin-top: 4px;
  overflow: hidden;
`;

const Details = styled.div`
  min-height: 42px;
  margin-top: 16px;
`;

const Strain = styled.div`
  background: #e3e7e9;
  border-radius: 12px;
  color: #2c3236;
  display: inline-block;
  font: 500 12px/1 ${({ theme }) => theme.customized.fonts.secondary};
  padding: 6px 8px;
`;

const Potency = styled.div<{ $isMissingStrain?: boolean }>`
  color: #646d72;
  font: 12px/1 ${({ theme }) => theme.customized.fonts.secondary};
  margin-top: 6px;

  ${({ $isMissingStrain }) =>
    $isMissingStrain &&
    css`
      padding-top: 24px;
    `}
`;

const Tags = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  line-height: ${13 / 12};
  margin-top: 16px;
  text-transform: ${({ theme }) => theme.customized.textTransforms.tags};
`;

const Price = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 4px;
  margin: 16px 0 8px;
  width: 100%;
`;

const ActivePrice = styled.div`
  color: #2c3236;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
`;

const Weight = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: ${20 / 14};
`;

const DiscountedPrice = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: var(--justify-content);
`;

const OriginalPrice = styled.div`
  color: #485055;
  font-size: 14px;
  line-height: 1;
  text-decoration: line-through;
`;

const Discount = styled.div`
  background: ${({ theme }) => theme.customized.colors.discountTag};
  border-radius: ${({ theme }) => theme.customized.radius.tags};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.5px;
  line-height: 1;
  margin-left: 2px;
  padding: 2px 6px;
  white-space: nowrap;
`;

const tintedButtonStyles = css`
  /* Hex alpha 10% */
  background: ${({ theme }) => theme.customized.colors.buttonsLinks}1a;
  color: ${({ theme }) => TinyColor.mix(theme.customized.colors.buttonsLinks, '#000', 20).toString()};
`;

const solidButtonStyles = css`
  background: ${({ theme }) => theme.customized.colors.buttonsLinks};
  color: #fff;
`;

const solidButtonHoverStyles = css`
  background: ${({ theme }) => TinyColor.mix(theme.customized.colors.buttonsLinks, '#000', 20).toString()};
  color: #fff;
`;

const buttonStyles = css`
  appearance: none;
  border: none;
  border-radius: ${({ theme }) => theme.customized.radius.buttons};
  cursor: pointer;
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: ${20 / 13};
  padding: 8px;
  position: relative;
  text-align: center;
  text-transform: ${({ theme }) => theme.customized.textTransforms.buttons};
  transition: all 0.2s;
  width: 100%;
  z-index: 1;

  ${({ theme }) =>
    theme.customized.colors.productCardButton === ProductCardButtonOption.tinted
      ? tintedButtonStyles
      : solidButtonStyles}

  &:hover {
    ${({ theme }) =>
      theme.customized.colors.productCardButton === ProductCardButtonOption.tinted
        ? solidButtonStyles
        : solidButtonHoverStyles}
  }
`;

const Button = styled.button`
  ${buttonStyles}
`;

const EmbeddedButton = styled(EmbeddedExternalLink)`
  ${buttonStyles}
`;
